import axios from "axios"

/**
 * Fetch cost codes with optional filtering
 * @param {string} token - Auth token
 * @param {Object} params - Query parameters
 * @returns {Promise<Array>} - List of cost codes
 */
export const fetchCostCodes = async (token, params = {}) => {
  const response = await axios.get("/api/myteconstruction/cost-codes", {
    headers: { Authorization: `Bearer ${token}` },
    params,
  })

  if (response.data.status !== "success") {
    throw new Error(response.data.message || "Failed to fetch cost codes")
  }

  return response.data.data
}

/**
 * Fetch cost code counts
 * @param {string} token - Auth token
 * @returns {Promise<Object>} - Counts object {total, active, inactive}
 */
/**
 * Fetch cost code counts with optional filtering
 * @param {string} token - Auth token
 * @param {Object} params - Query parameters (e.g., category, q, active)
 * @returns {Promise<Object>} - Counts object {total, active, inactive}
 */
export const fetchCostCodeCounts = async (token, params = {}) => {
  const response = await axios.get("/api/myteconstruction/cost-codes/counts", {
    headers: { Authorization: `Bearer ${token}` },
    params,
  })

  if (response.data.status !== "success") {
    throw new Error(response.data.message || "Failed to fetch cost code counts")
  }

  return response.data.data
}

/**
 * Fetch a single cost code by ID
 * @param {string} token - Auth token
 * @param {string} id - Cost code ID
 * @returns {Promise<Object>} - Cost code object
 */
export const fetchCostCodeById = async (token, id) => {
  const response = await axios.get(`/api/myteconstruction/cost-codes/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  })

  if (response.data.status !== "success") {
    throw new Error(response.data.message || "Failed to fetch cost code")
  }

  return response.data.data
}

/**
 * Create a new cost code
 * @param {string} token - Auth token
 * @param {Object} data - Cost code data
 * @returns {Promise<Object>} - Created cost code
 */
export const createCostCode = async (token, data) => {
  const response = await axios.post("/api/myteconstruction/cost-codes", data, {
    headers: { Authorization: `Bearer ${token}` },
  })

  if (response.data.status !== "success") {
    throw new Error(response.data.message || "Failed to create cost code")
  }

  return response.data.data
}

/**
 * Update an existing cost code
 * @param {string} token - Auth token
 * @param {string} id - Cost code ID
 * @param {Object} data - Updated cost code data
 * @returns {Promise<Object>} - Updated cost code
 */
export const updateCostCode = async (token, id, data) => {
  const response = await axios.put(`/api/myteconstruction/cost-codes/${id}`, data, {
    headers: { Authorization: `Bearer ${token}` },
  })

  if (response.data.status !== "success") {
    throw new Error(response.data.message || "Failed to update cost code")
  }

  return response.data.data
}

/**
 * Toggle cost code active status
 * @param {string} token - Auth token
 * @param {string} id - Cost code ID
 * @param {boolean} active - New active status
 * @returns {Promise<Object>} - Updated cost code
 */
export const toggleCostCodeActive = async (token, id, active) => {
  const response = await axios.patch(
    `/api/myteconstruction/cost-codes/${id}/active`,
    { active },
    { headers: { Authorization: `Bearer ${token}` } },
  )

  if (response.data.status !== "success") {
    throw new Error(response.data.message || "Failed to toggle cost code status")
  }

  return response.data.data
}

/**
 * Delete a cost code (only if not used)
 * @param {string} token - Auth token
 * @param {string} id - Cost code ID
 */
export const deleteCostCode = async (token, id) => {
  const response = await axios.delete(`/api/myteconstruction/cost-codes/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (response.data.status !== 'success') {
    throw new Error(response.data.message || 'Failed to delete cost code')
  }
}

/**
 * Bulk upsert cost codes
 * @param {string} token - Auth token
 * @param {Array} codes - Array of cost code objects {category, code, description}
 * @returns {Promise<Object>} - {upserted, modified}
 */
export const bulkUpsertCostCodes = async (token, codes) => {
  const response = await axios.post(
    "/api/myteconstruction/cost-codes/bulk",
    { codes },
    { headers: { Authorization: `Bearer ${token}` } }
  )
  if (response.data.status !== 'success') {
    throw new Error(response.data.message || 'Bulk upsert failed')
  }
  return response.data.data
}
